import { useEffect, useState } from "react";
import { Sparkles, Plus, Trash2, Loader2, Tag } from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "../../lib/api";

const money = (n) => `$${(Number(n) || 0).toLocaleString("es-MX", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Simulador del motor de promociones sobre un carrito de SKUs reales.
export default function RetailPromoSimulator({ moduleKey, item }) {
  const [products, setProducts] = useState([]);
  const [cart, setCart] = useState([]);
  const [pick, setPick] = useState("");
  const [result, setResult] = useState(null);
  const [busy, setBusy] = useState(false);

  useEffect(() => { api.get("/retail/products").then((r) => setProducts(r.data)).catch(() => {}); }, []);

  const add = () => {
    const p = products.find((x) => x.id === pick);
    if (!p) return;
    setCart((c) => c.find((x) => x.id === p.id) ? c.map((x) => x.id === p.id ? { ...x, qty: x.qty + 1 } : x)
      : [...c, { id: p.id, sku: p.sku, nombre: p.descripcion, precio: p.precio, qty: 1 }]);
    setResult(null);
  };
  const setQty = (id, q) => { setCart((c) => c.map((x) => x.id === id ? { ...x, qty: Math.max(1, q) } : x)); setResult(null); };
  const remove = (id) => { setCart((c) => c.filter((x) => x.id !== id)); setResult(null); };

  const simulate = async () => {
    if (!cart.length) { toast.error("Agrega productos al carrito"); return; }
    setBusy(true);
    try {
      const { data } = await api.post(`/retail/promotions/${item.id}/simulate`, { items: cart.map((c) => ({ sku: c.sku, nombre: c.nombre, precio: c.precio, qty: c.qty })) });
      setResult(data);
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || "Error"); } finally { setBusy(false); }
  };

  return (
    <div className="mt-4 border-t border-navy/10 pt-4" data-testid="promo-simulator">
      <div className="flex items-center gap-2 overline text-navy/40 mb-3"><Sparkles className="w-3.5 h-3.5" /> Simulador de promoción</div>
      <div className="flex gap-2 mb-3">
        <select value={pick} onChange={(e) => setPick(e.target.value)} data-testid="promo-sim-product"
          className="flex-1 field-sm">
          <option value="">Elige un producto…</option>
          {products.map((p) => <option key={p.id} value={p.id}>{p.sku} · {p.descripcion} · {money(p.precio)}</option>)}
        </select>
        <button onClick={add} disabled={!pick} className="btn btn-outline text-sm" data-testid="promo-sim-add"><Plus className="w-4 h-4" /> Agregar</button>
      </div>

      {cart.length > 0 && (
        <div className="space-y-2 mb-3">
          {cart.map((c) => (
            <div key={c.id} className="flex items-center gap-2 bg-white border border-navy/10 rounded-lg px-3 py-2" data-testid={`promo-sim-line-${c.sku}`}>
              <div className="min-w-0 flex-1"><div className="text-sm truncate">{c.nombre}</div><div className="text-[11px] text-navy/40">{c.sku} · {money(c.precio)}</div></div>
              <input type="number" min="1" value={c.qty} onChange={(e) => setQty(c.id, parseInt(e.target.value) || 1)} className="w-16 field-sm" />
              <button onClick={() => remove(c.id)} className="text-navy/30 hover:text-destructive"><Trash2 className="w-4 h-4" /></button>
            </div>
          ))}
          <button onClick={simulate} disabled={busy} className="btn btn-primary text-sm w-full" data-testid="promo-sim-run">
            {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Tag className="w-4 h-4" />} Simular promoción
          </button>
        </div>
      )}

      {result && (
        <div className="bg-cream border border-navy/10 rounded-lg p-4" data-testid="promo-sim-result">
          <div className="space-y-1 mb-3">
            {result.lines.map((l, i) => (
              <div key={i} className="flex justify-between text-sm">
                <span className="text-navy/70">{l.nombre} ×{l.qty}{l.promo ? ` · ${l.promo}` : ""}</span>
                <span>{l.descuento > 0 ? <span className="text-emerald-600">−{money(l.descuento)}</span> : money(l.importe)}</span>
              </div>
            ))}
          </div>
          <div className="border-t border-navy/10 pt-2 space-y-1 text-sm">
            <div className="flex justify-between text-navy/60"><span>Subtotal</span><span>{money(result.subtotal)}</span></div>
            <div className="flex justify-between text-emerald-600"><span>Descuento</span><span>−{money(result.descuento)}</span></div>
            <div className="flex justify-between text-navy/60"><span>IVA</span><span>{money(result.iva)}</span></div>
            <div className="flex justify-between font-medium text-base"><span>Total</span><span data-testid="promo-sim-total">{money(result.total)}</span></div>
          </div>
        </div>
      )}
    </div>
  );
}
